// Admin auth context — keeps the access + refresh tokens in localStorage and
// exposes `useAdminAuth()` to the rest of the dashboard.
import axios from "axios";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCESS_KEY = "sc_admin_access";
const REFRESH_KEY = "sc_admin_refresh";

const AdminAuthContext = createContext(null);

// axios instance dedicated to admin API — injects Authorization header + auto-refreshes
export const adminApi = axios.create({ baseURL: API });

adminApi.interceptors.request.use((config) => {
    const t = localStorage.getItem(ACCESS_KEY);
    if (t) config.headers.Authorization = `Bearer ${t}`;
    return config;
});

let refreshing = null;
adminApi.interceptors.response.use(
    (r) => r,
    async (err) => {
        const status = err?.response?.status;
        const original = err?.config;
        if (status === 401 && original && !original._retried) {
            const refresh = localStorage.getItem(REFRESH_KEY);
            if (!refresh) throw err;
            try {
                refreshing = refreshing || axios.post(`${API}/admin/auth/refresh`, { refresh_token: refresh });
                const { data } = await refreshing;
                refreshing = null;
                localStorage.setItem(ACCESS_KEY, data.access_token);
                original._retried = true;
                original.headers.Authorization = `Bearer ${data.access_token}`;
                return adminApi.request(original);
            } catch (e) {
                refreshing = null;
                localStorage.removeItem(ACCESS_KEY);
                localStorage.removeItem(REFRESH_KEY);
                throw e;
            }
        }
        throw err;
    },
);

export function AdminAuthProvider({ children }) {
    const [user, setUser] = useState(null);       // null = unknown, false = logged out, obj = logged in
    const [checking, setChecking] = useState(true);

    const refreshUser = useCallback(async () => {
        const t = localStorage.getItem(ACCESS_KEY);
        if (!t) {
            setUser(false);
            setChecking(false);
            return;
        }
        try {
            const { data } = await adminApi.get("/admin/auth/me");
            setUser(data);
        } catch {
            setUser(false);
        } finally {
            setChecking(false);
        }
    }, []);

    useEffect(() => { refreshUser(); }, [refreshUser]);

    // Idle auto-logout — signs the staff member out after 30 min of no interaction
    useEffect(() => {
        if (!user) return;
        let timer;
        const reset = () => {
            clearTimeout(timer);
            timer = setTimeout(() => logout(true), 30 * 60 * 1000);
        };
        const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
        events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
        reset();
        return () => {
            clearTimeout(timer);
            events.forEach((e) => window.removeEventListener(e, reset));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);

    const login = useCallback(async (email, password) => {
        const { data } = await adminApi.post("/admin/auth/login", { email, password });
        localStorage.setItem(ACCESS_KEY, data.access_token);
        localStorage.setItem(REFRESH_KEY, data.refresh_token);
        setUser(data.user);
        return data.user;
    }, []);

    const logout = useCallback(async (silent = false) => {
        try { if (!silent) await adminApi.post("/admin/auth/logout"); } catch (_) {}
        localStorage.removeItem(ACCESS_KEY);
        localStorage.removeItem(REFRESH_KEY);
        setUser(false);
    }, []);

    const value = useMemo(() => ({
        user, checking, login, logout, refreshUser,
        isSuperAdmin: user?.role === "super_admin",
        isReservationsManager: user?.role === "reservations_manager" || user?.role === "super_admin",
        isContentManager: user?.role === "content_manager" || user?.role === "super_admin",
    }), [user, checking, login, logout, refreshUser]);

    return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
    const ctx = useContext(AdminAuthContext);
    if (!ctx) throw new Error("useAdminAuth must be used inside AdminAuthProvider");
    return ctx;
}

// Turn any FastAPI error payload (string / obj / array) into a display string
export function formatApiError(err) {
    const detail = err?.response?.data?.detail;
    if (detail == null) return err?.message || "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => (e?.msg || JSON.stringify(e))).join(" · ");
    if (detail?.msg) return detail.msg;
    return String(detail);
}
